import { useEffect, useState, useRef } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { SkeletonLeaderboard } from "../components/ui/skeleton"
import { TeamPickersModal, TeamForModal } from "../components/TeamPickersModal"
import { calculateTeamPoints, calculateMaxPotential } from "../utils/scoring"
import { getActiveYear } from "../utils/activeYear"

interface Team {
  id: number
  name: string
  seed: number
  region: string
  is_eliminated: boolean
  wins: number
  points: number  // Calculated points based on wins
}

interface LeaderboardEntry {
  id: string
  entry_name: string
  points: number
  user_id: string
  display_name?: string
  picks?: Team[]
  teamsRemaining?: number
  rank?: number
}

interface PayoutConfig {
  place: number
  percentage: number
  amount?: number
}

export default function Leaderboard() {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [allTeams, setAllTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null)
  
  // Team modal state
  const [selectedTeam, setSelectedTeam] = useState<TeamForModal | null>(null)
  
  // URL params for deep linking
  const [searchParams, setSearchParams] = useSearchParams()
  const entryRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  
  // Payout state
  const [payouts, setPayouts] = useState<PayoutConfig[]>([])
  const [paidEntriesCount, setPaidEntriesCount] = useState(0)
  const [totalPot, setTotalPot] = useState(0)
  const [entryFee, setEntryFee] = useState<number>(20)
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [teamFilters, setTeamFilters] = useState<Set<number>>(new Set())
  const [showTeamDropdown, setShowTeamDropdown] = useState(false)
  
  // Comparison state
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set())
  const [showCompareModal, setShowCompareModal] = useState(false)
  
  // Active year
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  
  // Contest access control
  const [contestLive, setContestLive] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  
  // Last updated timestamp
  const [bracketUpdatedAt, setBracketUpdatedAt] = useState<string | null>(null)
  
  // Payout expansion state
  const [showPayoutDetails, setShowPayoutDetails] = useState(false)
  
  // Current user ID for "Your Entries" section
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [showMyEntries, setShowMyEntries] = useState(false)
  
  // Compare mode toggle
  const [compareMode, setCompareMode] = useState(false)

  useEffect(() => {
    async function loadLeaderboard() {
      const year = await getActiveYear()
      setActiveYearState(year)

      // Get entry fee and contest_live from years table
      const { data: yearData } = await supabase
        .from('years')
        .select('entry_fee, contest_live')
        .eq('year', year)
        .single()
      
      const fee = yearData?.entry_fee || 20
      setEntryFee(fee)
      setContestLive(yearData?.contest_live ?? false)

      // Get the latest game update time for this year
      const { data: latestGame } = await supabase
        .from('games')
        .select('updated_at')
        .eq('year', year)
        .eq('is_complete', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      
      setBracketUpdatedAt(latestGame?.updated_at || null)

      // Check if current user is admin and store user ID
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        setCurrentUserId(user.id)
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single()
        setIsAdmin(profile?.is_admin || false)
      }

      // Get all teams for filter dropdown (include wins for points calculation)
      const { data: teamsData } = await supabase
        .from('tournament_teams')
        .select('id, seed, region, is_eliminated, wins, school:team_schools(name)')
        .eq('year', year)
        .order('seed', { ascending: true })
      
      // Add calculated points to teams (flatten school name from join)
      // Points = Seed × Round Multipliers for each win
      const teamsWithPoints = (teamsData || []).map((t: any) => ({
        id: t.id,
        name: t.school?.name || 'Unknown',
        seed: t.seed,
        region: t.region,
        is_eliminated: t.is_eliminated,
        wins: t.wins || 0,
        points: calculateTeamPoints(t.seed || 1, t.wins || 0)
      }))
      setAllTeams(teamsWithPoints)

      // Get payout config for current year
      const { data: payoutData } = await supabase
        .from('payout_config')
        .select('place, percentage')
        .eq('year', year)
        .order('place', { ascending: true })

      // Count paid entries
      const { count: paidCount } = await supabase
        .from('entries')
        .select('*', { count: 'exact', head: true })
        .eq('year', year)
        .eq('is_paid', true)

      const paid = paidCount || 0
      const pot = paid * fee
      setPaidEntriesCount(paid)
      setTotalPot(pot)

      // Calculate actual payout amounts
      const payoutsWithAmounts = (payoutData || []).map((p: any) => ({
        place: p.place,
        percentage: p.percentage,
        amount: Math.round((p.percentage / 100) * pot)
      }))
      setPayouts(payoutsWithAmounts)

      // Get all entries
      const { data: entriesData, error: entriesError } = await supabase
        .from('entries')
        .select('id, entry_name, points, user_id')
        .eq('year', year)
        .order('points', { ascending: false })

      if (entriesError || !entriesData) {
        console.error('Error loading entries:', entriesError)
        setLoading(false)
        return
      }

      // Get unique user IDs and fetch profiles
      const userIds = Array.from(new Set(entriesData.map(e => e.user_id)))
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIds)

      const profileMap = new Map<string, string>()
      profilesData?.forEach(p => {
        if (p.display_name) profileMap.set(p.id, p.display_name)
      })

      // Get all entry picks with team info (include wins) - now using tournament_teams
      const entryIds = entriesData.map(e => e.id)
      const { data: picksData } = await supabase
        .from('entry_picks')
        .select('entry_id, team:tournament_teams(id, seed, region, is_eliminated, wins, school:team_schools(name))')
        .in('entry_id', entryIds)

      // Group picks by entry with points calculation
      const picksMap = new Map<string, Team[]>()
      picksData?.forEach((pick: { entry_id: string; team: any }) => {
        const rawTeam = Array.isArray(pick.team) ? pick.team[0] : pick.team
        if (!rawTeam) return
        
        // Add calculated points to team (flatten school name from join)
        const team: Team = {
          id: rawTeam.id,
          name: rawTeam.school?.name || 'Unknown',
          seed: rawTeam.seed,
          region: rawTeam.region,
          is_eliminated: rawTeam.is_eliminated,
          wins: rawTeam.wins || 0,
          points: calculateTeamPoints(rawTeam.seed || 1, rawTeam.wins || 0)
        }
        
        if (!picksMap.has(pick.entry_id)) {
          picksMap.set(pick.entry_id, [])
        }
        picksMap.get(pick.entry_id)!.push(team)
      })

      // Combine everything - calculate total points from team picks
      const entriesWithData = entriesData.map(entry => {
        const picks = picksMap.get(entry.id) || []
        const teamsRemaining = picks.filter(t => !t.is_eliminated).length
        // Calculate total points from all picked teams
        const calculatedPoints = picks.reduce((sum, t) => sum + t.points, 0)
        
        return {
          ...entry,
          points: calculatedPoints,  // Override with calculated points
          display_name: profileMap.get(entry.user_id),
          picks,
          teamsRemaining
        }
      })
      
      // Sort by calculated points
      entriesWithData.sort((a, b) => b.points - a.points)

      setEntries(entriesWithData)
      setLoading(false)
    }

    loadLeaderboard()
  }, [])

  // Handle URL param for auto-expanding an entry
  useEffect(() => {
    const expandId = searchParams.get('expand')
    if (expandId && entries.length > 0) {
      setExpandedEntry(expandId)
      // Scroll to the entry after a short delay
      setTimeout(() => {
        const ref = entryRefs.current.get(expandId)
        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      // Clear the param
      setSearchParams({})
    }
  }, [entries, searchParams, setSearchParams])

  const toggleExpand = (entryId: string) => {
    setExpandedEntry(expandedEntry === entryId ? null : entryId)
  }

  const handleTeamClick = (team: Team) => {
    setSelectedTeam({
      id: team.id,
      name: team.name,
      seed: team.seed,
      is_eliminated: team.is_eliminated
    })
  }

  const handleEntryClickFromModal = (entryId: string) => {
    setExpandedEntry(entryId)
    setTimeout(() => {
      const ref = entryRefs.current.get(entryId)
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  const toggleCompareSelection = (entryId: string) => {
    const newSelection = new Set(selectedForCompare)
    if (newSelection.has(entryId)) {
      newSelection.delete(entryId)
    } else if (newSelection.size < 2) {
      newSelection.add(entryId)
    }
    setSelectedForCompare(newSelection)
  }

  const clearComparison = () => {
    setSelectedForCompare(new Set())
    setShowCompareModal(false)
  }

  // Calculate ranks on FULL entries array first (handle ties)
  // This ensures ranks stay consistent even when filtering
  let currentRank = 0
  let previousPoints = -1
  const rankedAllEntries = entries.map((entry, index) => {
    if (entry.points !== previousPoints) {
      currentRank = index + 1
      previousPoints = entry.points
    }
    return { ...entry, rank: currentRank }
  })

  // Filter entries (ranks are already calculated from full field)
  const rankedEntries = rankedAllEntries.filter(entry => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      entry.entry_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (entry.display_name?.toLowerCase().includes(searchQuery.toLowerCase()))
    
    // Team filter - AND logic: entry must have ALL selected teams
    const matchesTeam = teamFilters.size === 0 || 
      Array.from(teamFilters).every(teamId => 
        entry.picks?.some(pick => pick.id === teamId)
      )
    
    return matchesSearch && matchesTeam
  })

  // Get selected team names for display
  const selectedTeamNames = allTeams.filter(t => teamFilters.has(t.id))

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {/* Skeleton Header */}
        <div>
          <div className="skeleton h-6 w-32 mb-2" />
          <div className="skeleton h-4 w-48" />
        </div>
        
        {/* Skeleton Prize Pool */}
        <div className="skeleton h-5 w-64" />
        
        {/* Skeleton Search */}
        <div className="flex gap-2">
          <div className="skeleton h-10 flex-1" />
          <div className="skeleton h-10 w-32" />
        </div>
        
        {/* Skeleton Leaderboard */}
        <SkeletonLeaderboard />
      </div>
    )
  }

  // Block access if contest is not live (admins can always view)
  if (!contestLive && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Contest Not Started</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          The leaderboard will be available once the contest goes live. 
          Check back after entries close!
        </p>
        <a 
          href="/dashboard" 
          className="px-6 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
        >
          Back to Dashboard
        </a>
      </div>
    )
  }

  // Helper to get payout for a rank
  const getPayoutForRank = (rank: number) => {
    const payout = payouts.find(p => p.place === rank)
    return payout?.amount || 0
  }

  // Format the bracket updated timestamp
  const formatUpdatedTime = (timestamp: string | null) => {
    if (!timestamp) return null
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-[#1e3a5f]">Leaderboard</h1>
        <p className="text-gray-500 text-sm">{entries.length} entries • Wright Picks {activeYear}</p>
      </div>

      {/* Prize Pool Summary - subtle inline */}
      {totalPot > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              💰 <span className="font-medium text-[#1e3a5f]">${totalPot.toLocaleString()}</span> prize pool • {paidEntriesCount} paid
            </span>
            <button 
              onClick={() => setShowPayoutDetails(!showPayoutDetails)}
              className="text-gray-500 text-xs hover:text-[#1e3a5f] flex items-center gap-1 transition-colors"
            >
              Top {payouts.length} places paid
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                className={`transition-transform ${showPayoutDetails ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
          
          {/* Expanded Payout Details */}
          {showPayoutDetails && payouts.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
              {payouts.map((payout, i) => (
                <div key={payout.place} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-base">
                      {payout.place === 1 ? '🥇' : payout.place === 2 ? '🥈' : payout.place === 3 ? '🥉' : '🏅'}
                    </span>
                    <span className="text-gray-600">
                      {payout.place === 1 ? '1st' : payout.place === 2 ? '2nd' : payout.place === 3 ? '3rd' : `${payout.place}th`} Place
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">({payout.percentage}%)</span>
                    <span className="font-semibold text-green-600">${payout.amount?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Last Updated - Links to Results page */}
      {bracketUpdatedAt && (
        <Link 
          to="/results"
          className="text-sm text-gray-500 flex items-center gap-1.5 hover:text-[#1e3a5f] transition-colors group"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 group-hover:text-[#1e3a5f]">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Results updated {formatUpdatedTime(bracketUpdatedAt)}
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 group-hover:text-[#1e3a5f]">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </Link>
      )}

      {/* Search and Filter Bar */}
      <div className="flex gap-2">
        {/* Search */}
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search entries or players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f]/30"
          />
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.3-4.3"/>
          </svg>
        </div>

        {/* Compare Mode Toggle */}
        <button
          onClick={() => {
            setCompareMode(!compareMode)
            if (compareMode) {
              setSelectedForCompare(new Set())
            }
          }}
          className={`h-10 px-3 flex items-center gap-1.5 text-sm border rounded-lg transition-colors ${
            compareMode 
              ? 'bg-[#F15A22] text-white border-[#F15A22]' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          }`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
          <span className="hidden sm:inline">Compare</span>
        </button>

        {/* Team Filter Dropdown - Multi-select */}
        <div className="relative">
          <button
            onClick={() => setShowTeamDropdown(!showTeamDropdown)}
            className={`h-10 px-3 flex items-center gap-2 text-sm border rounded-lg transition-colors ${
              teamFilters.size > 0 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {teamFilters.size > 0 ? `${teamFilters.size} Team${teamFilters.size > 1 ? 's' : ''}` : 'Filter by Team'}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          
          {showTeamDropdown && (
            <div className="absolute right-0 top-full mt-1 w-72 max-h-80 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              {/* Header with Clear All */}
              <div className="sticky top-0 bg-white border-b border-gray-100 p-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Select teams (AND filter)</span>
                {teamFilters.size > 0 && (
                  <button
                    onClick={() => setTeamFilters(new Set())}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Clear All
                  </button>
                )}
              </div>
              {allTeams.map(team => {
                const isSelected = teamFilters.has(team.id)
                return (
                  <button
                    key={team.id}
                    onClick={() => {
                      const newFilters = new Set(teamFilters)
                      if (isSelected) {
                        newFilters.delete(team.id)
                      } else {
                        newFilters.add(team.id)
                      }
                      setTeamFilters(newFilters)
                    }}
                    className={`w-full flex items-center gap-2 p-2 text-left hover:bg-gray-50 text-sm ${
                      isSelected ? 'bg-[#1e3a5f]/5' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected 
                        ? 'bg-[#1e3a5f] border-[#1e3a5f]' 
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                    {/* Status Dot */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      team.is_eliminated ? 'bg-red-400' : 'bg-green-500'
                    }`} />
                    {/* Seed */}
                    <span className={`text-xs font-semibold w-4 ${
                      team.is_eliminated ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {team.seed}
                    </span>
                    {/* Team Name */}
                    <span className={`flex-1 truncate ${
                      team.is_eliminated ? 'text-gray-400 line-through' : 'text-gray-700'
                    }`}>
                      {team.name}
                    </span>
                    {/* Region */}
                    <span className="text-xs text-gray-400">{team.region}</span>
                  </button>
                )
              })}
              {/* Done button at bottom */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 p-2">
                <button
                  onClick={() => setShowTeamDropdown(false)}
                  className="w-full py-2 bg-[#1e3a5f] text-white text-sm font-medium rounded-lg hover:bg-[#153050]"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters */}
      {(searchQuery || teamFilters.size > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-500">Showing {rankedEntries.length} of {entries.length} entries</span>
          {selectedTeamNames.map(team => (
            <button
              key={team.id}
              onClick={() => {
                const newFilters = new Set(teamFilters)
                newFilters.delete(team.id)
                setTeamFilters(newFilters)
              }}
              className="flex items-center gap-1.5 px-2 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-xs"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${team.is_eliminated ? 'bg-red-400' : 'bg-green-500'}`} />
              {team.name}
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          ))}
          {teamFilters.size > 1 && (
            <button
              onClick={() => setTeamFilters(new Set())}
              className="text-xs text-gray-500 hover:text-red-500"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Your Entries Section - Collapsible */}
      {currentUserId && (() => {
        const myRankedEntries = rankedEntries.filter(e => e.user_id === currentUserId)
        if (myRankedEntries.length === 0) return null
        
        return (
          <div className="bg-gradient-to-r from-[#1e3a5f]/5 to-transparent rounded-lg border border-[#1e3a5f]/10">
            <button
              onClick={() => setShowMyEntries(!showMyEntries)}
              className="w-full flex items-center justify-between px-3 py-2 text-left"
            >
              <span className="text-sm font-medium text-[#1e3a5f]">
                Your Entries ({myRankedEntries.length})
              </span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                className={`text-gray-400 transition-transform ${showMyEntries ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            
            {showMyEntries && (
              <div className="px-3 pb-3 space-y-2">
                {myRankedEntries.map(entry => {
                  const maxPotential = entry.picks ? calculateMaxPotential(entry.picks) : null
                  return (
                    <button
                      key={entry.id}
                      onClick={() => {
                        setExpandedEntry(entry.id)
                        setTimeout(() => {
                          const ref = entryRefs.current.get(entry.id)
                          if (ref) {
                            ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }
                        }, 100)
                      }}
                      className="w-full flex items-center gap-3 p-2 bg-white rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      {/* Rank */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                        entry.rank === 1 ? 'bg-amber-400 text-amber-900' :
                        entry.rank === 2 ? 'bg-gray-300 text-gray-700' :
                        entry.rank === 3 ? 'bg-amber-600 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {entry.rank}
                      </div>
                      
                      {/* Entry name */}
                      <span className="flex-1 font-medium text-[#1e3a5f] text-sm truncate">
                        {entry.entry_name}
                      </span>
                      
                      {/* Teams alive */}
                      <span className={`w-6 text-right text-sm font-semibold tabular-nums ${
                        entry.teamsRemaining === 0 ? 'text-gray-400' :
                        (entry.teamsRemaining ?? 0) > 10 ? 'text-emerald-600' :
                        (entry.teamsRemaining ?? 0) > 5 ? 'text-amber-600' :
                        'text-rose-500'
                      }`}>
                        {entry.teamsRemaining}
                      </span>
                      
                      {/* Points */}
                      <span className="w-9 text-right text-sm font-bold text-[#1e3a5f] tabular-nums">
                        {entry.points}
                      </span>
                      
                      {/* Max */}
                      <span className="w-9 text-right text-sm text-gray-400 tabular-nums">
                        {maxPotential ? maxPotential.max : entry.points}
                      </span>
                      
                      {/* Arrow */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })()}

      {/* Compare Button (shows when 2+ selected) */}
      {selectedForCompare.size >= 2 && (
        <div className="sticky top-16 z-10 flex items-center gap-2 p-3 bg-[#1e3a5f] rounded-lg shadow-lg">
          <span className="text-white text-sm flex-1">
            {selectedForCompare.size} entries selected
          </span>
          <button
            onClick={clearComparison}
            className="px-3 py-1.5 text-sm font-medium border border-white/50 text-white rounded-md hover:bg-white/20 transition-colors"
          >
            Clear
          </button>
          <Button
            size="sm"
            onClick={() => setShowCompareModal(true)}
            className="bg-[#F15A22] hover:bg-[#e04d15] text-white"
          >
            Compare
          </Button>
        </div>
      )}

      {/* Empty State */}
      {rankedEntries.length === 0 && (
        <Card className="bg-white border-0 shadow-sm">
          <div className="py-12 text-center text-gray-500">
            <p className="font-medium">{entries.length === 0 ? "No entries yet" : "No matching entries"}</p>
            <p className="text-sm mt-1">{entries.length === 0 ? "Be the first to join the pool!" : "Try a different search or filter"}</p>
          </div>
        </Card>
      )}

      {/* Leaderboard Section Header */}
      {rankedEntries.length > 0 && (
        <div className="flex items-center justify-between mb-1 pr-3 sm:pr-4">
          <span className="text-sm font-medium text-gray-500">All Entries</span>
          {/* Column labels - aligned with grid below */}
          <div className="flex items-center text-[10px] text-gray-400 uppercase tracking-wide">
            <span className="w-7 text-right">Alive</span>
            <span className="w-10 text-right">Pts</span>
            <span className="w-10 text-right">Max</span>
            {/* Spacer for arrow column */}
            <span className="w-6 sm:w-8"></span>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      {rankedEntries.length > 0 && (
        <Card className="bg-white border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {rankedEntries.map((entry, index) => {
              const entryMaxPotential = entry.picks ? calculateMaxPotential(entry.picks) : null
              return (
              <div 
                key={entry.id}
                ref={(el) => { if (el) entryRefs.current.set(entry.id, el) }}
              >
                {/* Entry Row */}
                <div className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-gray-50 transition-colors ${
                  index !== rankedEntries.length - 1 ? 'border-b border-gray-100' : ''
                } ${expandedEntry === entry.id ? 'bg-blue-50' : ''} ${
                  compareMode && selectedForCompare.has(entry.id) 
                    ? 'bg-[#F15A22]/10 border-l-4 border-l-[#F15A22]' 
                    : ''
                }`}>
                  {/* Main clickable area - using grid for guaranteed column widths */}
                  <button
                    onClick={() => compareMode ? toggleCompareSelection(entry.id) : toggleExpand(entry.id)}
                    className="flex-1 grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 sm:gap-3 text-left"
                  >
                    {/* Rank */}
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 ${
                      entry.rank === 1 ? 'bg-amber-400 text-amber-900' :
                      entry.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      entry.rank === 3 ? 'bg-amber-600 text-white' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {entry.rank}
                    </div>

                    {/* Entry Info - grid column 1fr forces truncation */}
                    <div className="min-w-0 overflow-hidden">
                      <div className="font-semibold text-[#1e3a5f] text-sm sm:text-base truncate">
                        {entry.entry_name}
                        {entry.rank === 1 && <span className="ml-1">🥇</span>}
                        {entry.rank === 2 && <span className="ml-1">🥈</span>}
                        {entry.rank === 3 && <span className="ml-1">🥉</span>}
                      </div>
                      <div className="text-gray-500 text-xs truncate">{entry.display_name || 'Player'}</div>
                    </div>

                    {/* Stats - separate fixed width columns for perfect alignment */}
                    <div className="flex items-center">
                      {/* Teams alive */}
                      <span className={`w-7 text-right text-sm sm:text-base font-bold tabular-nums ${
                        entry.teamsRemaining === 0 ? 'text-gray-400' :
                        (entry.teamsRemaining ?? 0) > 10 ? 'text-emerald-600' :
                        (entry.teamsRemaining ?? 0) > 5 ? 'text-amber-600' :
                        'text-rose-500'
                      }`}>
                        {entry.teamsRemaining}
                      </span>
                      
                      {/* Points */}
                      <span className="w-10 text-right text-sm sm:text-base font-bold text-[#1e3a5f] tabular-nums">
                        {entry.points}
                      </span>
                      
                      {/* Max */}
                      <span className="w-10 text-right text-sm sm:text-base text-gray-400 tabular-nums">
                        {entryMaxPotential ? entryMaxPotential.max : entry.points}
                      </span>
                    </div>

                    {/* Expand Arrow */}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      className={`text-gray-400 transition-transform ${expandedEntry === entry.id ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                </div>

                {/* Expanded Picks */}
                {expandedEntry === entry.id && entry.picks && (() => {
                  const maxPotential = calculateMaxPotential(entry.picks)
                  return (
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                      <div className="text-xs text-gray-500 mb-2 font-medium">
                        {entry.entry_name}'s Picks ({entry.picks.length} teams)
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                        {entry.picks
                          .sort((a, b) => a.seed - b.seed)
                          .map(team => (
                            <button 
                              key={team.id}
                              onClick={() => handleTeamClick(team)}
                              className={`flex items-center gap-1.5 p-1.5 rounded text-xs text-left hover:ring-2 hover:ring-[#1e3a5f]/30 transition-all ${
                                team.is_eliminated 
                                  ? 'bg-red-50/50' 
                                  : team.points > 0 
                                    ? 'bg-green-50'
                                    : 'bg-white'
                              }`}
                            >
                              {/* Status dot */}
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                team.is_eliminated ? 'bg-red-400' : 'bg-emerald-500'
                              }`} />
                              {/* Seed number */}
                              <span className={`text-[11px] font-semibold w-4 ${
                                team.is_eliminated ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {team.seed}
                              </span>
                              <span className={`truncate flex-1 font-medium ${team.is_eliminated ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                {team.name}
                              </span>
                              <span className={`text-[11px] ${team.is_eliminated ? 'text-gray-300' : 'text-gray-400'}`}>
                                {team.wins}W
                              </span>
                              <span className={`font-semibold min-w-[20px] text-right ${
                                team.is_eliminated ? 'text-gray-300' : team.points > 0 ? 'text-green-600' : 'text-gray-300'
                              }`}>
                                {team.points}
                              </span>
                            </button>
                          ))}
                      </div>
                      {/* Max Potential Summary */}
                      <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                          {entry.teamsRemaining} team{entry.teamsRemaining !== 1 ? 's' : ''} alive
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-600">
                            <span className="font-semibold text-[#1e3a5f]">{maxPotential.current}</span> pts now
                          </span>
                          {maxPotential.potential > 0 && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-gray-500">
                                +{maxPotential.potential} potential
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="text-gray-600">
                                <span className="font-semibold text-blue-600">{maxPotential.max}</span> max
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Team Pickers Modal */}
      {selectedTeam && (
        <TeamPickersModal
          team={selectedTeam}
          onClose={() => setSelectedTeam(null)}
          onEntryClick={handleEntryClickFromModal}
        />
      )}

      {/* Comparison Modal */}
      {showCompareModal && (
        <ComparisonModal
          entries={entries.filter(e => selectedForCompare.has(e.id))}
          onClose={() => setShowCompareModal(false)}
        />
      )}
    </div>
  )
}

function ComparisonModal({ entries, onClose }: { entries: LeaderboardEntry[]; onClose: () => void }) {
  // Get all unique teams from all entries
  const allTeamIds = new Set<number>()
  entries.forEach(e => e.picks?.forEach(p => allTeamIds.add(p.id)))
  
  // Group teams by how many entries picked them
  const teamPickCounts = new Map<number, number>()
  const teamData = new Map<number, Team>()
  
  entries.forEach(entry => {
    entry.picks?.forEach(team => {
      teamData.set(team.id, team)
      teamPickCounts.set(team.id, (teamPickCounts.get(team.id) || 0) + 1)
    })
  })
  
  // Separate into shared and unique picks
  const sharedPicks: Team[] = []
  const uniquePicks = new Map<string, Team[]>() // entry_id -> teams only they picked
  
  entries.forEach(entry => {
    uniquePicks.set(entry.id, [])
  })
  
  Array.from(teamData.values()).forEach(team => {
    const count = teamPickCounts.get(team.id) || 0
    if (count === entries.length) {
      sharedPicks.push(team)
    } else {
      entries.forEach(entry => {
        if (entry.picks?.some(p => p.id === team.id)) {
          uniquePicks.get(entry.id)?.push(team)
        }
      })
    }
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="bg-white w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-4 bg-white border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="font-bold text-lg text-gray-900">Entry Comparison</div>
            <div className="text-gray-500 text-sm">{entries.length} entries selected</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-80px)]">
          {/* Entry Headers with Points */}
          <div className="grid gap-4 p-4 bg-gray-50 border-b" style={{ gridTemplateColumns: `repeat(${entries.length}, 1fr)` }}>
            {entries.map((entry, i) => {
              const maxPotential = entry.picks ? calculateMaxPotential(entry.picks) : null
              return (
                <div key={entry.id} className="text-center min-w-0">
                  {/* Points and Max side by side */}
                  <div className="flex items-baseline justify-center gap-3">
                    <div>
                      <div className={`text-2xl font-bold tabular-nums ${
                        i === 0 ? 'text-[#1e3a5f]' : 'text-[#F15A22]'
                      }`}>
                        {entry.points}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">pts</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold tabular-nums text-gray-400">
                        {maxPotential ? maxPotential.max : entry.points}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">max</div>
                    </div>
                  </div>
                  <div className="font-semibold text-gray-900 text-sm truncate mt-1">{entry.entry_name}</div>
                  {entry.display_name && (
                    <div className="text-gray-400 text-xs truncate">{entry.display_name}</div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Shared Picks */}
          {sharedPicks.length > 0 && (
            <div className="p-4 bg-green-50/30">
              <div className="text-xs uppercase tracking-wide text-green-700 mb-2 font-semibold">
                Both Picked ({sharedPicks.length})
              </div>
              <div className="grid grid-cols-2 gap-1">
                {sharedPicks.sort((a, b) => a.seed - b.seed).map(team => (
                  <div 
                    key={team.id}
                    className={`flex items-center gap-1.5 p-1.5 rounded text-xs ${
                      team.is_eliminated 
                        ? 'bg-red-50/50' 
                        : 'bg-white'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      team.is_eliminated ? 'bg-red-400' : 'bg-emerald-500'
                    }`} />
                    <span className={`text-[11px] font-semibold w-4 ${
                      team.is_eliminated ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {team.seed}
                    </span>
                    <span className={`truncate flex-1 font-medium ${
                      team.is_eliminated ? 'text-gray-400 line-through' : 'text-gray-700'
                    }`}>
                      {team.name}
                    </span>
                    <span className={`text-[11px] ${team.is_eliminated ? 'text-gray-300' : 'text-gray-400'}`}>
                      {team.wins}W
                    </span>
                    <span className={`font-semibold min-w-[20px] text-right ${
                      team.is_eliminated ? 'text-gray-300' : team.points > 0 ? 'text-green-600' : 'text-gray-300'
                    }`}>
                      {team.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unique Picks per Entry */}
          <div className="grid border-t" style={{ gridTemplateColumns: `repeat(${entries.length}, 1fr)` }}>
            {entries.map((entry, i) => {
              const unique = uniquePicks.get(entry.id) || []
              const bgColor = i === 0 ? 'bg-blue-50/20' : 'bg-orange-50/20'
              
              return (
                <div key={entry.id} className={`p-3 ${bgColor} ${i > 0 ? 'border-l' : ''} min-w-0`}>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold truncate">
                    Only {entry.entry_name} ({unique.length})
                  </div>
                  <div className="space-y-1">
                    {unique.sort((a, b) => a.seed - b.seed).map(team => (
                      <div 
                        key={team.id}
                        className={`flex items-center gap-1.5 p-1.5 rounded text-xs ${
                          team.is_eliminated 
                            ? 'bg-red-50/50' 
                            : 'bg-white'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          team.is_eliminated ? 'bg-red-400' : 'bg-emerald-500'
                        }`} />
                        <span className={`text-[11px] font-semibold w-4 ${
                          team.is_eliminated ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {team.seed}
                        </span>
                        <span className={`truncate flex-1 font-medium ${
                          team.is_eliminated ? 'text-gray-400 line-through' : 'text-gray-700'
                        }`}>
                          {team.name}
                        </span>
                        <span className={`text-[11px] ${team.is_eliminated ? 'text-gray-300' : 'text-gray-400'}`}>
                          {team.wins}W
                        </span>
                        <span className={`font-semibold min-w-[20px] text-right ${
                          team.is_eliminated ? 'text-gray-300' : team.points > 0 ? 'text-green-600' : 'text-gray-300'
                        }`}>
                          {team.points}
                        </span>
                      </div>
                    ))}
                    {unique.length === 0 && (
                      <div className="text-gray-400 text-xs italic">No unique picks</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Card>
    </div>
  )
}
